import { api } from '@/internationalization/api-client'
import type { 
  ActionItem,
  ActionItemMeeting,
  ActionItemStatus,
  AssigneeCandidate,
  Paginated,
} from '@/types'

/**
 * How the patron names an assignee. At most one of `id` and `email`.
 *
 * The patron deliberately does NOT decide whether an address belongs to a
 * participant, a colleague with a seat, or somebody outside the workspace — the
 * server resolves that, because deciding here would mean the browser holding
 * the workspace's member list, and that list is the answer to "who works here".
 *
 * An empty object unassigns.
 */
export interface AssigneePayload {
  id?: string
  email?: string
  name?: string
}

export interface ActionItemPatch {
  status?: ActionItemStatus
  /** An ISO date-time. Null clears the due date. */
  dueDate?: string | null
  text?: string
  assignee?: AssigneePayload | null
  /**
   * `{inherit: true}` follows the meeting again; `{patronId: x}` pins one;
   * `{patronId: null}` means deliberately no patron. Those are three different
   * requests and one nullable field cannot express them.
   */
  patron?: { inherit: true } | { patronId: string | null }
}

/** If-Match makes a stale tab's edit a 409 rather than a silent overwrite. */
function versioned(rowVersion?: number) {
  return rowVersion ? { headers: { 'If-Match': `"${rowVersion}"` } } : undefined
}

/**
 * Every filter the action list can apply. All of them are server-side, so the
 * total in the response is the total for the filtered set.
 */
export interface ActionListParams {
  /** Index signature so the params object doubles as a react-query key. */
  [key: string]: string | undefined
  status?: string
  owner?: string
  patron?: string
  /** A meeting id, or 'all'. */
  meetingId?: string
  /** Narrows to a single item -- a notification's deep-link. */
  actionItemId?: string
  series?: string
  /** 'all' | 'overdue' | 'today' | 'this_week' | 'none'. */
  due?: string
  /**
   * The VIEWER'S calendar day, as `YYYY-MM-DD`.
   *
   * Sent because `dueDate` is a DAY, and the server's day is UTC: after 18:30
   * in India the UTC date is still yesterday, so a server-side "today" would
   * empty this filter every evening. Always sent, not only with `due`, so the
   * query key changes at midnight and a tab left open overnight refetches
   * instead of showing yesterday's buckets.
   */
  today?: string
}

export const actionsApi = {
  list: (params: ActionListParams) =>
    api.get<Paginated<ActionItem>>('/actions', { query: { ...params } }),

  /** The meetings that have action items — the "filter by meeting" options. */
  meetings: () => api.get<ActionItemMeeting[]>('/actions/meetings'),

  create: (body: {
    meetingId: string
    text: string
    dueDate?: string | null
    assignee?: AssigneePayload
  }) => api.post<ActionItem>('/actions', body),

  update: (id: string, patch: ActionItemPatch, rowVersion?: number) =>
    api.patch<ActionItem>(`/actions/${id}`, patch, versioned(rowVersion)),

  setStatus: (id: string, status: ActionItemStatus, rowVersion?: number) =>
    api.patch<ActionItem>(`/actions/${id}`, { status }, versioned(rowVersion)),

  remove: (id: string) => api.delete<void>(`/actions/${id}`),

  /** The meeting's roster first, then the workspace's active members. */
  assignees: (meetingId?: string) =>
    api.get<AssigneeCandidate[]>('/actions/assignees', {
      query: meetingId ? { meetingId } : undefined,
    }),
}
